/**
 * ContractAiPanel — Panel IA affiché en haut de ContractDetail :
 * TL;DR, risques légaux + timeline visuelle du cycle de vie.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ShieldAlert, ShieldCheck, AlertTriangle,
         Calendar, Repeat, TrendingUp, CircleDot, Scale } from "lucide-react";
import { cn } from "@/lib/utils";
import ClauseNegotiatorDialog from "@/components/ClauseNegotiatorDialog";
import { AiDiscrepanciesCard } from "@/components/contracts/AiDiscrepanciesCard";

const RISK_LABEL = {
  clause_abusive_l442: "Clause abusive (L442-1)",
  reconduction_chatel: "Reconduction tacite (Chatel)",
  rgpd_dpa_manquant: "DPA RGPD manquant",
  non_concurrence_sans_indemnite: "Non-concurrence sans indemnité",
  penalites_disproportionnees: "Pénalités disproportionnées",
  resiliation_unilaterale: "Résiliation unilatérale",
  exclusivite_excessive: "Exclusivité excessive",
  prix_indeterme: "Prix indéterminé",
};

export default function ContractAiPanel({ contract, onRefresh, onAnalyze, canWrite }) {
  const [negotiateRisk, setNegotiateRisk] = useState(null);
  const hasAi = !!contract.ai_summary || !!contract.ai_extracted_at;
  const hasSource = !!contract.file_asset_id || (contract.documents || []).some(
    (d) => /\.(pdf|jpe?g|png|webp)$/i.test(d.name || ""));

  if (!hasAi) {
    return hasSource && canWrite ? (
      <Card className="p-4 border-blue-200 bg-blue-50/40 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Analyser avec l'IA</div>
            <div className="text-xs text-slate-500">
              Résumé, risques légaux, obligations, indexation + complétion des champs vides depuis le PDF du contrat
            </div>
          </div>
        </div>
        <Button onClick={onAnalyze} className="bg-blue-600 hover:bg-blue-700"
                data-testid="ai-analyze-btn">
          <Sparkles className="h-4 w-4 mr-1.5" /> Lancer l'analyse
        </Button>
      </Card>
    ) : null;
  }

  const risks = contract.ai_risk_flags || [];
  const score = contract.ai_risk_score || 0;

  return (
    <div className="space-y-3">
      <AiDiscrepanciesCard contract={contract} onRefresh={onRefresh} canWrite={canWrite} />
      {/* TL;DR + score */}
      <Card className="p-4 border-blue-200 bg-blue-50/40">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-blue-700 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Résumé exécutif
          </div>
          <div className="flex items-center gap-2">
            <RiskScore score={score} />
            {canWrite && (
              <Button size="sm" variant="ghost" onClick={onAnalyze}
                      className="h-7 text-xs" data-testid="ai-reanalyze-btn">
                <Sparkles className="h-3 w-3" />
                <span className="ml-1">Ré-analyser</span>
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          {contract.ai_summary || "—"}
        </p>
      </Card>

      {/* Risks */}
      {risks.length > 0 && (
        <Card className="p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" /> Risques légaux détectés · {risks.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {risks.map((r, i) => (
              <div key={i} className={cn("rounded-lg border p-2",
                r.severity === "high" ? "border-red-200 bg-red-50/40"
                : r.severity === "medium" ? "border-amber-200 bg-amber-50/40"
                : "border-emerald-200 bg-emerald-50/40")}
                   data-testid={`risk-${r.code}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900">
                      {RISK_LABEL[r.code] || r.code}
                    </div>
                    {r.explanation && (
                      <div className="text-[11px] text-slate-600 mt-0.5">{r.explanation}</div>
                    )}
                    {r.quote && (
                      <blockquote className="text-[11px] text-slate-500 italic mt-1 border-l-2 border-slate-300 pl-2 truncate">
                        « {r.quote} »
                      </blockquote>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setNegotiateRisk(r)}
                            className="mt-1.5 h-6 text-[10px] text-amber-700 hover:text-amber-900 hover:bg-amber-100 px-2"
                            data-testid={`negotiate-btn-${r.code}`}>
                      <Scale className="h-3 w-3 mr-1" /> Améliorer cette clause
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Timeline */}
      <ContractTimeline contract={contract} />

      {/* Obligations */}
      {(contract.ai_obligations || []).length > 0 && (
        <Card className="p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
            Obligations clés
          </div>
          <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
            {contract.ai_obligations.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </Card>
      )}

      <ClauseNegotiatorDialog
        open={!!negotiateRisk}
        onOpenChange={(v) => { if (!v) setNegotiateRisk(null); }}
        contract={contract}
        risk={negotiateRisk}
      />
    </div>
  );
}

function RiskScore({ score }) {
  const tone = score >= 80 ? "critical" : score >= 51 ? "high" : score >= 21 ? "medium" : "low";
  const cls = {
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-amber-50 text-amber-800 border-amber-200",
    high: "bg-orange-50 text-orange-700 border-orange-200",
    critical: "bg-red-50 text-red-700 border-red-200",
  }[tone];
  const label = { low: "Sain", medium: "À surveiller", high: "Risqué", critical: "Critique" }[tone];
  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium", cls)}
         data-testid="ai-risk-score">
      {tone === "low" ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
      <span className="tabular-nums font-semibold">{score}</span>
      <span>· {label}</span>
    </div>
  );
}

function ContractTimeline({ contract }) {
  const fmt = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
    { day: "2-digit", month: "short", year: "numeric" }) : null;
  const today = new Date().toISOString().slice(0, 10);
  const events = [
    { key: "signature", date: contract.date_signature || contract.ai_extracted?.signature_date,
      icon: FileSignatureIcon, label: "Signature" },
    { key: "start", date: contract.start_date, icon: Calendar, label: "Prise d'effet" },
    { key: "notice", date: contract.preavis_effective_date,
      icon: Repeat, label: `Préavis (${contract.cancellation_notice_days || 30}j)` },
    { key: "end", date: contract.end_date || contract.date_fin, icon: TrendingUp, label: "Fin" },
  ].filter((e) => e.date);

  if (events.length < 2) return null;

  return (
    <Card className="p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-4">
        Cycle de vie
      </div>
      <div className="relative">
        <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-slate-200 -translate-y-1/2" />
        <div className="relative flex justify-between">
          {events.map((e, i) => {
            const past = e.date < today;
            const Icon = e.icon;
            return (
              <div key={i} className="flex flex-col items-center gap-2 relative bg-white px-2"
                   data-testid={`timeline-${e.key}`}>
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center border-2",
                  past ? "bg-blue-100 border-blue-300 text-blue-700"
                       : "bg-slate-50 border-slate-300 text-slate-500"
                )}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                </div>
                <div className="text-center">
                  <div className="text-[10px] font-medium text-slate-700 uppercase tracking-wide">{e.label}</div>
                  <div className="text-[11px] text-slate-500 tabular-nums">{fmt(e.date)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function FileSignatureIcon(props) {
  // Fallback icon since we already import Calendar/Repeat/TrendingUp
  return <CircleDot {...props} />;
}
